import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ConfirmationModal, ListItem, Popover, SvgIcon } from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { ResourceList } from '@portal/common/types';

import EditResourceListModal from '../../components/EditResourceListModal';
import _ from '../../locale';
import { getOrganizationId } from '../../store/organization/selectors';
import {
    deleteOrganizationResourceListAsync,
    makeResourceListDefaultAsync,
    makeResourceListNotDefaultAsync,
    makeResourceListPrivateAsync,
    makeResourceListPublicAsync,
} from '../../store/resource-lists/actions';
import {
    getDefaultList,
    getPublicLists as getPublicListsSelector,
} from '../../store/resource-lists/selectors';
import { isManager as isManagerSelector } from '../../store/session/selectors';

const ListItemWithSeparator = styled(ListItem)(({ theme }) => ({
    borderTop: theme.border.tableRowSeparator,
}));

type Props = {
    listId: string;
    list: ResourceList;
    isCreatedByMe: boolean;
};

const ListSettings: FC<Props> = ({ listId, list, isCreatedByMe }) => {
    const isManager = useSelector(isManagerSelector);
    const organizationId = useSelector(getOrganizationId);
    const publicLists = useSelector(getPublicListsSelector);
    const defaultList = useSelector(getDefaultList);

    const dispatch = useDispatch();
    const createDispatchAction = (action: any) =>
        useCallback((payload: any) => dispatch(action.request(payload)), []);
    const makeListPublic = createDispatchAction(makeResourceListPublicAsync);
    const makeListPrivate = createDispatchAction(makeResourceListPrivateAsync);
    const deleteOrganizationResourceList = createDispatchAction(
        deleteOrganizationResourceListAsync
    );
    const makeListDefault = createDispatchAction(makeResourceListDefaultAsync);
    const makeListNotDefault = createDispatchAction(makeResourceListNotDefaultAsync);

    const [isVisibleEditListModal, setIsVisibleEditListModal] = useState<boolean>(false);
    const [isVisibleRemoveListModal, setIsVisibleRemoveListModal] = useState<boolean>(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    const isDefault = useMemo(() => {
        return defaultList && defaultList.id === listId;
    }, [defaultList, listId]);

    const isPublic = useMemo(() => {
        return (publicLists || []).find((list) => list.id === listId);
    }, [publicLists, listId]);

    const hideDropdown = useCallback(() => {
        dropdownRef.current?.setPopoverVisibility(false);
    }, []);

    const showEditListModal = useCallback(() => {
        hideDropdown();
        setIsVisibleEditListModal(true);
    }, [hideDropdown]);

    const hideEditListModal = useCallback(() => {
        setIsVisibleEditListModal(false);
    }, []);

    const showRemoveListModal = useCallback(() => {
        hideDropdown();
        setIsVisibleRemoveListModal(true);
    }, [hideDropdown]);

    const hideRemoveListModal = useCallback(() => {
        setIsVisibleRemoveListModal(false);
    }, []);

    const handleRemoveList = useCallback(() => {
        deleteOrganizationResourceList({
            organizationId,
            listId,
        });
    }, [organizationId, deleteOrganizationResourceList, listId]);

    const handleSubmitRemoveList = useCallback(() => {
        hideRemoveListModal();
        handleRemoveList();
    }, [hideRemoveListModal, handleRemoveList]);

    const toggleDefault = useCallback(() => {
        if (isDefault) {
            makeListNotDefault({ organizationId, listId });
        } else {
            makeListDefault({ organizationId, listId });
        }
    }, [organizationId, makeListDefault, makeListNotDefault, isDefault, listId]);

    const togglePublicity = useCallback(() => {
        if (isPublic) {
            makeListPrivate({ organizationId, listId });
        } else {
            makeListPublic({ organizationId, listId });
        }
    }, [organizationId, makeListPublic, makeListPrivate, isPublic, listId]);

    if (!list || !(isCreatedByMe || isManager)) {
        return null;
    }

    const areBaseSettingsAvailable = isCreatedByMe || isManager;

    return (
        <>
            <Popover
                ref={dropdownRef}
                renderContent={() => (
                    <>
                        {areBaseSettingsAvailable && (
                            <>
                                <ListItem onClick={showEditListModal}>
                                    {_.get('lists_edit_modal_title')}
                                </ListItem>

                                <ListItem onClick={showRemoveListModal}>
                                    {_.get('lists_remove_modal_title')}
                                </ListItem>
                            </>
                        )}
                        {isManager && (
                            <ListItemWithSeparator onClick={togglePublicity}>
                                {_.get(
                                    isPublic ? 'lists_make_list_private' : 'lists_make_list_public'
                                )}
                            </ListItemWithSeparator>
                        )}
                        {isManager && isPublic && (
                            <ListItem onClick={toggleDefault}>
                                {_.get(
                                    isDefault
                                        ? 'lists_make_list_non_default'
                                        : 'lists_make_list_default'
                                )}
                            </ListItem>
                        )}
                    </>
                )}
            >
                {({ ref, toggleVisibility }) => (
                    <div ref={ref}>
                        <SvgIcon
                            size="large"
                            height={20}
                            src="/icons/ic-settings.svg"
                            tooltip={_('tooltip_settings')}
                            color={(theme) => theme.color.green1}
                            onClick={toggleVisibility}
                        />
                    </div>
                )}
            </Popover>
            <EditResourceListModal
                item={list}
                isVisible={isVisibleEditListModal}
                onClose={hideEditListModal}
                onSuccess={hideEditListModal}
            />

            <ConfirmationModal
                title={_.get('lists_remove_modal_title')}
                message={_.get('lists_remove_modal_question')}
                isVisible={isVisibleRemoveListModal}
                onClose={hideRemoveListModal}
                onSubmit={handleSubmitRemoveList}
            />
        </>
    );
};

export default ListSettings;
